import { useState } from 'react';
import { Alert, App, Button, Form, Input, Modal, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, NetworkError } from '../../api/client';
import {
  OTP_CHANNEL_LABELS,
  formatPhoneNational,
  memberKeys,
  membersApi,
  type MemberProfile360,
  type PhoneChangeOtpResponse,
} from './api';

type Step = 'phone' | 'otp';

interface PhoneForm {
  newPhone: string;
}

interface OtpForm {
  code: string;
}

export function ChangePhoneModal({
  member,
  onClose,
}: {
  member: MemberProfile360;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [phoneForm] = Form.useForm<PhoneForm>();
  const [otpForm] = Form.useForm<OtpForm>();

  const [step, setStep] = useState<Step>('phone');
  const [newPhone, setNewPhone] = useState('');
  const [otpInfo, setOtpInfo] = useState<PhoneChangeOtpResponse | null>(null);

  const reportError = (error: unknown, fallback: string) => {
    message.error(
      error instanceof ApiError || error instanceof NetworkError ? error.message : fallback,
    );
  };

  const requestMutation = useMutation({
    mutationFn: (phone: string) => membersApi.requestPhoneChangeOtp(member.id, phone),
    onSuccess: (data, phone) => {
      setNewPhone(phone);
      setOtpInfo(data);
      setStep('otp');
      otpForm.resetFields();
    },
    onError: (error) => reportError(error, 'Không gửi được mã xác thực.'),
  });

  const confirmMutation = useMutation({
    mutationFn: (code: string) => membersApi.confirmPhoneChange(member.id, newPhone, code),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: memberKeys.all });
      message.success(
        `Đã đổi số từ ${formatPhoneNational(result.oldPhone)} sang ${formatPhoneNational(result.newPhone)}.`,
      );
      onClose();
    },
    onError: (error) => reportError(error, 'Không đổi được số điện thoại.'),
  });

  const submitPhone = (values: PhoneForm) => {
    requestMutation.mutate(values.newPhone.trim());
  };

  const submitOtp = (values: OtpForm) => {
    confirmMutation.mutate(values.code.trim());
  };

  const backToPhone = () => {
    setStep('phone');
    setOtpInfo(null);
    otpForm.resetFields();
  };

  const minutes = otpInfo ? Math.max(1, Math.round(otpInfo.expiresInSeconds / 60)) : 0;

  return (
    <Modal
      title="Đổi số điện thoại"
      open
      onCancel={onClose}
      footer={null}
      destroyOnClose
      maskClosable={!requestMutation.isPending && !confirmMutation.isPending}
    >
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Mã xác thực gửi về <strong>số mới</strong>, không gửi về{' '}
        {formatPhoneNational(member.phone)}. Đổi số là chuyển cả ví điểm sang số đó.
      </Typography.Paragraph>

      {step === 'phone' && (
        <Form
          form={phoneForm}
          layout="vertical"
          onFinish={submitPhone}
          requiredMark={false}
          initialValues={{ newPhone: newPhone }}
        >
          <Form.Item
            name="newPhone"
            label="Số điện thoại mới"
            rules={[{ required: true, message: 'Nhập số điện thoại mới.' }]}
          >
            <Input
              placeholder="0908123456"
              inputMode="tel"
              autoComplete="tel"
              autoFocus
              aria-label="Số điện thoại mới"
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={requestMutation.isPending}>
              Gửi mã xác thực
            </Button>
          </Form.Item>
        </Form>
      )}

      {step === 'otp' && otpInfo && (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Đã gửi mã tới ${formatPhoneNational(newPhone)} qua ${OTP_CHANNEL_LABELS[otpInfo.sentVia]}. Mã hết hạn sau ${minutes} phút.`}
          />
          {otpInfo.fellBack && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Kênh đã chọn không tới được nên hệ thống đã gửi qua tin nhắn SMS. Kiểm tra hộp thư tin nhắn, không ngồi đợi trên Zalo."
            />
          )}
          <Form form={otpForm} layout="vertical" onFinish={submitOtp} requiredMark={false}>
            <Form.Item
              name="code"
              label="Mã xác thực"
              rules={[{ required: true, message: 'Nhập mã vừa gửi.' }]}
            >
              <Input
                placeholder="6 số"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                autoFocus
                aria-label="Mã xác thực"
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={confirmMutation.isPending}>
                Đổi số
              </Button>
              <Button
                type="link"
                onClick={() => requestMutation.mutate(newPhone)}
                loading={requestMutation.isPending}
              >
                Gửi lại mã
              </Button>
              <Button type="link" onClick={backToPhone} disabled={confirmMutation.isPending}>
                Đổi số khác
              </Button>
            </Form.Item>
          </Form>
        </>
      )}
    </Modal>
  );
}
